import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const VendorAuthContext = createContext(null);

export function VendorAuthProvider({ children }) {
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('vendor_token');

  const fetchProfile = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get('/vendor/auth/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendor(res.data);
    } catch {
      localStorage.removeItem('vendor_token');
      setVendor(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post('/vendor/auth/login', { email, password });
    localStorage.setItem('vendor_token', res.data.token);
    setVendor(res.data.vendor);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    const token = getToken();
    if (token) {
      try {
        await axios.post('/vendor/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (error) {
        if (error.response?.status !== 401) {
          toast.error('Could not securely sign out. Please try again.');
          return false;
        }
      }
    }
    localStorage.removeItem('vendor_token');
    setVendor(null);
    return true;
  }, []);

  return (
    <VendorAuthContext.Provider value={{ vendor, loading, login, logout }}>
      {children}
    </VendorAuthContext.Provider>
  );
}

export function useVendorAuth() {
  const ctx = useContext(VendorAuthContext);
  if (!ctx) throw new Error('useVendorAuth must be inside VendorAuthProvider');
  return ctx;
}

export function getVendorAuthHeader() {
  const token = localStorage.getItem('vendor_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}
